/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import io from 'socket.io-client';
import api from '../../services/api';
import Button from '../../components/common/Button';

const ConferenceRoom = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  
  const [meeting, setMeeting] = useState(null);
  const [peers, setPeers] = useState({});
  const [localStream, setLocalStream] = useState(null);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [privateChatMessages, setPrivateChatMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('global');
  const [messageInput, setMessageInput] = useState('');
  const [canProduce, setCanProduce] = useState(false);
  
  const localVideoRef = useRef();
  const peersRef = useRef({});
  const socketRef = useRef(null);
  const localStreamRef = useRef(null);

  useEffect(() => {
    const loadMeetingDetails = async () => {
      try {
        const response = await api.get('/meetings');
        const foundMeeting = response.data.find(m => m.roomId === roomId);
        
        if (!foundMeeting) {
          alert('Meeting not found');
          navigate('/');
          return;
        }
        
        setMeeting(foundMeeting);
        
        const userCanProduce = 
          user.role === 'ADMIN' || 
          (user.role === 'SPEAKER' && foundMeeting.speakers?.some(s => s.speakerId === user.id));
        
        setCanProduce(userCanProduce);
        
        if (userCanProduce) {
          await initializeMedia();
        }
        
        initializeSocket(userCanProduce);
      } catch (error) {
        console.error('Failed to fetch meeting:', error);
        alert('Failed to load meeting');
        navigate('/');
      }
    };

    loadMeetingDetails();
    
    return () => {
      cleanup();
    };
  }, [navigate, roomId, user.id, user.role]);

  const initializeMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: true,
      });
      
      localStreamRef.current = stream;
      setLocalStream(stream);
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      console.log('Local media initialized');
    } catch (error) {
      console.error('Failed to get media:', error);
      alert('Failed to access camera/microphone. Please grant permissions and reload.');
    }
  };

  const initializeSocket = (userCanProduce) => {
    const token = localStorage.getItem('token');
    
    const newSocket = io('http://localhost:3010', {
      auth: { token },
    });

    socketRef.current = newSocket;

    newSocket.on('connect', () => {
      console.log('Connected to server');
      newSocket.emit('join-room', { roomId });
    });

    newSocket.on('joined-room', ({ canProduce: serverCanProduce }) => {
      console.log('Successfully joined room, canProduce:', serverCanProduce);
      setCanProduce(serverCanProduce);
    });

    newSocket.on('user-joined', ({ userId, canProduce: userCanProduce }) => {
      console.log('New user joined:', userId, 'canProduce:', userCanProduce);
      
      if (userId === user.id) {
        console.log('Ignoring self join event');
        return;
      }
      
      if (localStreamRef.current && canProduce) {
        console.log('Creating peer connection for:', userId);
        createPeerConnection(userId, true);
      }
    });

    newSocket.on('offer', async ({ from, offer }) => {
      console.log('Received offer from:', from);
      
      if (from === user.id) {
        console.log('Ignoring offer from self');
        return;
      }
      
      await handleOffer(from, offer);
    });

    newSocket.on('answer', async ({ from, answer }) => {
      console.log('Received answer from:', from);
      
      if (from === user.id) {
        console.log('Ignoring answer from self');
        return;
      }
      
      const peer = peersRef.current[from];
      if (peer) {
        try {
          await peer.setRemoteDescription(new RTCSessionDescription(answer));
          console.log('Set remote description from answer');
        } catch (error) {
          console.error('Error setting remote description:', error);
        }
      }
    });

    newSocket.on('ice-candidate', async ({ from, candidate }) => {
      if (from === user.id) return;
      
      const peer = peersRef.current[from];
      if (peer && candidate) {
        try {
          await peer.addIceCandidate(new RTCIceCandidate(candidate));
          console.log('Added ICE candidate from:', from);
        } catch (error) {
          console.error('Error adding ICE candidate:', error);
        }
      }
    });

    newSocket.on('user-left', ({ userId }) => {
      console.log('User left:', userId);
      removePeer(userId);
    });

    newSocket.on('global-chat', (message) => {
      setChatMessages(prev => [...prev, message]);
    });

    newSocket.on('private-chat', (message) => {
      setPrivateChatMessages(prev => [...prev, message]);
    });

    newSocket.on('error', ({ message }) => {
      console.error('Socket error:', message);
      alert(message);
    });
  };

  const createPeerConnection = (userId, isInitiator) => {
    console.log('Creating peer connection for:', userId, 'initiator:', isInitiator);
    
    const peerConnection = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
      ],
    });

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        peerConnection.addTrack(track, localStreamRef.current);
        console.log('Added local track to peer connection:', track.kind);
      });
    }

    peerConnection.ontrack = (event) => {
      console.log('Received remote track from:', userId, event.track.kind);
      
      setPeers(prev => ({
        ...prev,
        [userId]: {
          connection: peerConnection,
          stream: event.streams[0],
        },
      }));
    };

    peerConnection.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        socketRef.current.emit('ice-candidate', {
          to: userId,
          candidate: event.candidate,
          roomId,
        });
      }
    };

    peerConnection.onconnectionstatechange = () => {
      console.log('Connection state:', peerConnection.connectionState, 'for:', userId);
      
      if (peerConnection.connectionState === 'failed' || 
          peerConnection.connectionState === 'disconnected') {
        console.log('Peer connection failed/disconnected for:', userId);
      }
    };

    peersRef.current[userId] = peerConnection;

    if (isInitiator) {
      createOffer(userId, peerConnection);
    }

    return peerConnection;
  };

  const createOffer = async (userId, peerConnection) => {
    try {
      const offer = await peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });
      
      await peerConnection.setLocalDescription(offer);
      console.log('Created and set local offer for:', userId);
      
      socketRef.current.emit('offer', {
        to: userId,
        offer,
        roomId,
      });
    } catch (error) {
      console.error('Failed to create offer:', error);
    }
  };

  const handleOffer = async (from, offer) => {
    console.log('Handling offer from:', from);
    const peerConnection = createPeerConnection(from, false);
    
    try {
      await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
      console.log('Set remote description from offer');
      
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);
      console.log('Created and set local answer');
      
      socketRef.current.emit('answer', {
        to: from,
        answer,
        roomId,
      });
    } catch (error) {
      console.error('Failed to handle offer:', error);
    }
  };

  const removePeer = (userId) => {
    if (peersRef.current[userId]) {
      peersRef.current[userId].close();
      delete peersRef.current[userId];
    }
    
    setPeers(prev => {
      const newPeers = { ...prev };
      delete newPeers[userId];
      return newPeers;
    });
  };

  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioOn(audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOn(videoTrack.enabled);
      }
    }
  };

  const sendMessage = () => {
    if (!messageInput.trim() || !socketRef.current) return;
    
    const eventName = activeTab === 'global' ? 'global-chat' : 'private-chat';
    
    socketRef.current.emit(eventName, {
      roomId,
      message: messageInput,
    });
    
    setMessageInput('');
  };

  const leaveMeeting = () => {
    cleanup();
    navigate(-1);
  };

  const cleanup = () => {
    console.log('Cleaning up...');
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('Stopped track:', track.kind);
      });
    }
    
    Object.values(peersRef.current).forEach(peer => {
      peer.close();
    });
    
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
  };

  const styles = {
    container: {
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.colors.background,
    },
    header: {
      padding: '16px 24px',
      backgroundColor: theme.colors.headerBg,
      borderBottom: `1px solid ${theme.colors.border}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      color: theme.colors.text,
    },
    main: {
      flex: 1,
      display: 'flex',
      overflow: 'hidden',
    },
    videoSection: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      overflow: 'auto',
    },
    videoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '16px',
      flex: 1,
    },
    videoWrapper: {
      position: 'relative',
      backgroundColor: '#000',
      borderRadius: '8px',
      overflow: 'hidden',
      height: '100%',
      minHeight: '200px',
    },
    video: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    videoLabel: {
      position: 'absolute',
      bottom: '8px',
      left: '8px',
      padding: '4px 12px',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      color: '#fff',
      borderRadius: '4px',
      fontSize: '14px',
    },
    controls: {
      display: 'flex',
      justifyContent: 'center',
      gap: '12px',
      padding: '20px',
      backgroundColor: theme.colors.backgroundSecondary,
      borderTop: `1px solid ${theme.colors.border}`,
    },
    controlBtn: (active) => ({
      padding: '12px 24px',
      borderRadius: '6px',
      backgroundColor: active ? theme.colors.error : theme.colors.backgroundSecondary,
      border: `2px solid ${theme.colors.border}`,
      color: active ? '#fff' : theme.colors.text,
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
    }),
    chatPanel: {
      width: '350px',
      borderLeft: `1px solid ${theme.colors.border}`,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.colors.backgroundSecondary,
    },
    chatTabs: {
      display: 'flex',
      borderBottom: `1px solid ${theme.colors.border}`,
    },
    tab: (active) => ({
      flex: 1,
      padding: '12px',
      textAlign: 'center',
      cursor: 'pointer',
      backgroundColor: active ? theme.colors.background : 'transparent',
      color: active ? theme.colors.primary : theme.colors.textSecondary,
      fontWeight: active ? '600' : '400',
      fontSize: '14px',
      borderBottom: active ? `2px solid ${theme.colors.primary}` : 'none',
      transition: 'all 0.2s',
    }),
    chatMessages: {
      flex: 1,
      padding: '16px',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    message: {
      padding: '8px 12px',
      borderRadius: '8px',
      backgroundColor: theme.colors.cardBg,
      fontSize: '14px',
    },
    messageSender: {
      fontWeight: '600',
      color: theme.colors.primary,
      marginBottom: '4px',
      fontSize: '12px',
    },
    messageText: {
      color: theme.colors.text,
      lineHeight: '1.4',
    },
    messageTime: {
      fontSize: '11px',
      color: theme.colors.textSecondary,
      marginTop: '4px',
    },
    chatInput: {
      display: 'flex',
      padding: '16px',
      gap: '8px',
      borderTop: `1px solid ${theme.colors.border}`,
    },
    input: {
      flex: 1,
      padding: '10px 14px',
      borderRadius: '6px',
      border: `1px solid ${theme.colors.border}`,
      backgroundColor: theme.colors.inputBg,
      color: theme.colors.text,
      fontSize: '14px',
    },
    noAccess: {
      textAlign: 'center',
      padding: '40px 20px',
      color: theme.colors.textSecondary,
      fontSize: '16px',
    },
    status: {
      fontSize: '12px',
      color: theme.colors.textSecondary,
      marginTop: '4px',
    },
  };

  if (!meeting) {
    return (
      <div style={styles.container}>
        <div style={styles.noAccess}>Loading meeting...</div>
      </div>
    );
  }

  const peerCount = Object.keys(peers).length;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{meeting.title}</h1>
          <div style={styles.status}>
            {canProduce ? 'Broadcasting' : 'Watching'} • {peerCount} participant{peerCount !== 1 ? 's' : ''}
          </div>
        </div>
        <Button variant="danger" size="small" onClick={leaveMeeting}>
          Leave Meeting
        </Button>
      </div>

      <div style={styles.main}>
        <div style={styles.videoSection}>
          <div style={styles.videoGrid}>
            {canProduce && localStream && (
              <div style={styles.videoWrapper}>
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={styles.video}
                />
                <div style={styles.videoLabel}>You {user.role === 'ADMIN' ? '(Admin)' : '(Speaker)'}</div>
              </div>
            )}

            {Object.entries(peers).map(([userId, peerData]) => (
              peerData.stream && (
                <div key={userId} style={styles.videoWrapper}>
                  <video
                    autoPlay
                    playsInline
                    ref={el => {
                      if (el && el.srcObject !== peerData.stream) {
                        el.srcObject = peerData.stream;
                      }
                    }}
                    style={styles.video}
                  />
                  <div style={styles.videoLabel}>Participant</div>
                </div>
              )
            ))}

            {!canProduce && peerCount === 0 && (
              <div style={styles.noAccess}>
                Waiting for speakers to join...
              </div>
            )}

            {canProduce && !localStream && (
              <div style={styles.noAccess}>
                Initializing camera and microphone...
              </div>
            )}
          </div>

          {canProduce && localStream && (
            <div style={styles.controls}>
              <button
                style={styles.controlBtn(!isAudioOn)}
                onClick={toggleAudio}
              >
                {isAudioOn ? 'Mute Mic' : 'Unmute Mic'}
              </button>
              <button
                style={styles.controlBtn(!isVideoOn)}
                onClick={toggleVideo}
              >
                {isVideoOn ? 'Stop Video' : 'Start Video'}
              </button>
            </div>
          )}
        </div>

        <div style={styles.chatPanel}>
          <div style={styles.chatTabs}>
            <div
              style={styles.tab(activeTab === 'global')}
              onClick={() => setActiveTab('global')}
            >
              Global Chat
            </div>
            <div
              style={styles.tab(activeTab === 'private')}
              onClick={() => setActiveTab('private')}
            >
              Questions
            </div>
          </div>

          <div style={styles.chatMessages}>
            {(activeTab === 'global' ? chatMessages : privateChatMessages).length === 0 ? (
              <div style={styles.noAccess}>
                {activeTab === 'global' ? 'No messages yet' : 'No questions yet'}
              </div>
            ) : (
              (activeTab === 'global' ? chatMessages : privateChatMessages).map((msg, index) => (
                <div key={index} style={styles.message}>
                  <div style={styles.messageSender}>
                    {msg.userName} ({msg.userRole})
                  </div>
                  <div style={styles.messageText}>{msg.message}</div>
                  <div style={styles.messageTime}>
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={styles.chatInput}>
            <input
              style={styles.input}
              type="text"
              placeholder={activeTab === 'global' ? 'Message everyone...' : 'Ask a question...'}
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            />
            <Button size="small" onClick={sendMessage}>
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConferenceRoom;
